import React, { ReactNode, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useAuth } from "@noom/hive-auth";
import { evalConditionalPageProp } from "src/hooks/useActivePageProps";
import { getActivePage } from "src/pageDefinitions/pageSets";
import { CoreReduxState } from "./redux/store";
import { captureException } from "./error";
import { trackAndRedirectToLogin } from "./auth";
import { NoomLoader } from "src/components/noom-loader/NoomLoader";

function evaluateAuthGuard(
  state: CoreReduxState,
  location: any
): boolean | undefined {
  try {
    // PageSet-level props are normalized onto each page during registration
    const { activePage } = getActivePage(location.pathname, state, location);
    const { authGuard } = activePage?.props || {};
    return evalConditionalPageProp(authGuard, state);
  } catch (error) {
    captureException(error, "failed-to-evaluate-auth-guard");
    return false;
  }
}

export function PageSetAuthGuard({ children }: { children: ReactNode }) {
  const location = useLocation<{ pageSetId: string }>();
  const { isAuthenticated, shouldReLogin } = useAuth();
  const needsAuthGuard = useSelector((state: CoreReduxState) =>
    evaluateAuthGuard(state, location)
  );

  const shouldRedirect =
    needsAuthGuard && (!isAuthenticated || shouldReLogin());
  const redirectingRef = useRef(false);

  useEffect(() => {
    if (shouldRedirect && !redirectingRef.current) {
      redirectingRef.current = true;
      trackAndRedirectToLogin();
    }
  }, [shouldRedirect]);

  if (shouldRedirect) {
    return <NoomLoader fullScreen />;
  }
  return <>{children}</>;
}

declare module "@pageDefinitions/types" {
  export interface PageSetPageConfigProps {
    readonly authGuard?: ConditionalPageProp<boolean>;
  }
}
